import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LTButton, LTInputBox, LTText } from '@lambdatestincprivate/lt-components';
import styles from './landing.module.css';

/**
 * Shown when there is no ?pack= param: drag-drop anywhere on the page, a
 * file picker, or a pasted pack URL.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Landing(_ref) {
  var onFile = _ref.onFile,
    onUrl = _ref.onUrl;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dragging = _useState2[0],
    setDragging = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    url = _useState4[0],
    setUrl = _useState4[1];
  var inputRef = useRef(null);
  var dragDepth = useRef(0);
  useEffect(function () {
    var onDragEnter = function onDragEnter(e) {
      var _e$dataTransfer;
      if (!((_e$dataTransfer = e.dataTransfer) !== null && _e$dataTransfer !== void 0 && _e$dataTransfer.types.includes('Files'))) return;
      dragDepth.current += 1;
      setDragging(true);
    };
    var onDragLeave = function onDragLeave() {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    var onDragOver = function onDragOver(e) {
      return e.preventDefault();
    };
    var onDrop = function onDrop(e) {
      var _e$dataTransfer2;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      var file = (_e$dataTransfer2 = e.dataTransfer) === null || _e$dataTransfer2 === void 0 || (_e$dataTransfer2 = _e$dataTransfer2.files) === null || _e$dataTransfer2 === void 0 ? void 0 : _e$dataTransfer2[0];
      if (file) onFile(file);
    };
    var onKey = function onKey(e) {
      if (e.key === 'Escape') {
        dragDepth.current = 0;
        setDragging(false);
      }
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    window.addEventListener('keydown', onKey);
    return function () {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('keydown', onKey);
    };
  }, [onFile]);
  var onPick = useCallback(function (e) {
    var _e$target$files;
    var file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) onFile(file);
    e.target.value = '';
  }, [onFile]);
  var submitUrl = useCallback(function (e) {
    e.preventDefault();
    var trimmed = url.trim();
    if (trimmed) onUrl(trimmed);
  }, [onUrl, url]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.wrap,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.card,
      children: [/*#__PURE__*/_jsx(LTText, {
        as: "h1",
        text: "Evidence Viewer",
        color: "var(--ev-fg)",
        className: styles.title,
        style: {
          fontSize: '20px',
          lineHeight: '28px',
          fontWeight: 600
        }
      }), /*#__PURE__*/_jsxs(LTText, {
        as: "p",
        variant: LTText.TextVariant.SUBHEADER_REGULAR,
        color: "var(--ev-fg-muted)",
        className: styles.subtitle,
        children: ["Open a sealed ", /*#__PURE__*/_jsx("code", {
          children: ".evidence"
        }), " pack \u2014 everything renders from the file itself, nothing is uploaded."]
      }), /*#__PURE__*/_jsxs("button", {
        type: "button",
        className: styles.dropZone,
        onClick: function onClick() {
          var _inputRef$current;
          return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
        },
        children: [/*#__PURE__*/_jsx(LTText, {
          text: "Drop a .evidence file anywhere",
          variant: LTText.TextVariant.SUBHEADER_BOLD,
          color: "inherit"
        }), /*#__PURE__*/_jsx(LTText, {
          text: "or click to choose a file",
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--ev-fg-muted)"
        })]
      }), /*#__PURE__*/_jsx("input", {
        ref: inputRef,
        type: "file",
        accept: ".evidence,application/zip",
        className: "ev-sr-only",
        "aria-label": "Choose a .evidence file",
        onChange: onPick
      }), /*#__PURE__*/_jsxs("form", {
        className: styles.urlForm,
        onSubmit: submitUrl,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.urlField,
          children: /*#__PURE__*/_jsx(LTInputBox
          // type="text": native url validation would reject the relative
          // paths (/packs/x.evidence) normalizePackInput explicitly
          // supports
          , {
            type: "text",
            inputMode: "url",
            inputOnly: true,
            name: "pack-url",
            ariaLabel: "Pack URL",
            placeholder: "\u2026or paste a pack URL (?pack=)",
            size: LTInputBox.Size.MEDIUM,
            variant: LTInputBox.Variant.WHITE,
            value: url,
            onChange: function onChange(value) {
              return setUrl(value);
            }
            // The built-in clear action only resets LTInputBox's internal
            // state — mirror it here or Open would submit the stale URL.
            ,
            trailingAction: function trailingAction() {
              return setUrl('');
            }
          })
        }), /*#__PURE__*/_jsx(LTButton, {
          label: "Open",
          variant: LTButton.Variant.DEFAULT,
          size: LTButton.Size.MEDIUM,
          onClick: function onClick() {}
        })]
      })]
    }), dragging && /*#__PURE__*/_jsx("div", {
      className: styles.dragOverlay,
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx("span", {
        className: "ev-t-header20",
        children: "Drop to open"
      })
    })]
  });
}